import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState, useCallback, createContext, useMemo, Suspense, lazy } from 'react';
import { ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import arEG from 'antd/locale/ar_EG';
import PrivateRoute from './auth/privateRoute';
import Login from './login/index';
import LayoutMenu from './layout/index';
import Store, { StoreContext } from './store/index';
import { lightTheme, darkTheme } from './theme';
import SplashScreen from './components/SplashScreen';

// Route-level code splitting: everything post-auth is a separate chunk so the
// initial download stays small. Login stays eager (it's the entry point for
// anonymous visitors); the splash screen doubles as Suspense fallback while
// chunks load in. Importer functions are exported so we can prefetch them
// in the background after auth resolves — the first navigation to each
// route then lands on a warm cache.
const importMain = () => import('./main/index');
const importShifts = () => import('./shifts/shifts');
const importNews = () => import('./news');
const importAdminPanel = () => import('./adminPanel');
const importProfile = () => import('./profile');
const importStats = () => import('./stats');
const importTeams = () => import('./teams');
const importTeamDetail = () => import('./teams/TeamDetail');
const importTestLogin = () => import('./testLogin/index');
const Main = lazy(importMain);
const Shifts = lazy(importShifts);
const News = lazy(importNews);
const AdminPanel = lazy(importAdminPanel);
const Profile = lazy(importProfile);
const Stats = lazy(importStats);
const Teams = lazy(importTeams);
const TeamDetail = lazy(importTeamDetail);
const TestLogin = lazy(importTestLogin);

// Isolated context so theme changes only re-render ConfigProvider,
// not every StoreContext consumer.
export const ThemeContext = createContext({ darkMode: false, toggleDarkMode: () => {} });

const antdLocaleMap = {
  es: esES,
  en: enUS,
  ar: arEG,
};

// Normalize shift URLs: backend may store /shifts/<id> (path style) but the
// app's router only has /shifts with the id as a bare query string (?<id>).
const normalizeUrl = url => {
  try {
    const { pathname, search } = new URL(url);
    const match = pathname.match(/^\/shifts\/(.+)$/);
    if (match) return `/shifts?${match[1]}`;
    return pathname + search;
  } catch {
    return '/';
  }
};

function ThemedApp() {
  const { darkMode } = useContext(ThemeContext);
  const store = useContext(StoreContext);
  const {
    user: [user],
    language: [language],
    isStoreLoading: [isStoreLoading],
  } = store;
  const navigate = useNavigate();

  const selectedLang = user?.languageSelected ?? language ?? 'es';
  const antdLocale = antdLocaleMap[Object.keys(antdLocaleMap).find(k => selectedLang.includes(k))] ?? esES;

  // Handle notification clicks — service worker posts SW_NAVIGATE instead of
  // opening a new tab, so we navigate in-place (no reload, no duplicate FCM listener)
  useEffect(() => {
    if (!navigator.serviceWorker) return undefined;
    const handler = event => {
      if (event.data?.type !== 'SW_NAVIGATE') return;
      navigate(normalizeUrl(event.data.url));
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // Once auth resolves, warm up the other route chunks in the background so
  // navigating from the current route to another lands on a warm cache.
  // Firefox/Safari lack requestIdleCallback — fall back to a delayed setTimeout.
  useEffect(() => {
    if (!user?.uid) return undefined;
    const schedule = window.requestIdleCallback
      ?? (cb => window.setTimeout(cb, 800));
    const cancel = window.cancelIdleCallback
      ?? (id => window.clearTimeout(id));
    const id = schedule(() => {
      importShifts();
      importProfile();
      importStats();
      importNews();
      importMain();
      importTeams();
      if (user.isAdmin) importAdminPanel();
    }, { timeout: 3000 });
    return () => cancel(id);
  }, [user?.uid, user?.isAdmin]);

  return (
    <ConfigProvider theme={darkMode ? darkTheme : lightTheme} locale={antdLocale}>
      {/* Splash screen — blocks the UI while auth/store is resolving */}
      <SplashScreen visible={isStoreLoading} />
      <div style={{ visibility: 'hidden' }} id="recaptcha-container" />
      <Suspense fallback={<SplashScreen visible />}>
        <Routes>
          <Route
            exact
            path="/"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <Main />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/admin"
            element={(
              <PrivateRoute adminOnly>
                <LayoutMenu>
                  <AdminPanel />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/shifts"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <Shifts />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/news"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <News />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/profile"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <Profile />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/stats"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <Stats />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/teams"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <Teams />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route
            exact
            path="/teams/:id"
            element={(
              <PrivateRoute>
                <LayoutMenu>
                  <TeamDetail />
                </LayoutMenu>
              </PrivateRoute>
            )}
          />
          <Route path="/login" element={<Login />} />
          {process.env.REACT_APP_USE_EMULATORS === 'true' && (
            <Route path="/test-login" element={<TestLogin />} />
          )}
        </Routes>
      </Suspense>
    </ConfigProvider>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('darkMode') === 'true',
  );

  // Persist and apply theme class whenever darkMode changes.
  // Runs synchronously via layout effect so body class is set before paint.
  useEffect(() => {
    localStorage.setItem('darkMode', String(darkMode));
    document.body.classList.toggle('dark', darkMode);
    document.body.classList.toggle('light', !darkMode);
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => setDarkMode(prev => !prev), []);
  const themeValue = useMemo(() => ({ darkMode, toggleDarkMode }), [darkMode, toggleDarkMode]);

  return (
    <ThemeContext.Provider value={themeValue}>
      <Router>
        <Store>
          <ThemedApp />
        </Store>
      </Router>
    </ThemeContext.Provider>
  );
}

export default App;
